import { type RefObject, useCallback, useState } from 'react';
import { useToast } from '@adc/parallax-component-library';
import { IconStatusIncorrect } from '@adc/parallax-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { useAdminUserCreateMutation } from '~/api/query/adminUserCreate';
import type {
  AdminUserFormRef,
  UserSubmitValues,
} from '~/components/forms/adminUser/AdminUserForm';
import { useModal } from '~/features/modals/useModal';
import { Route } from '~/routes/hcp-admin/users/$userId';

type UseAdminUserCreateFormOptions = {
  formRef: RefObject<AdminUserFormRef | null>;
};

export const useAdminUserCreateForm = ({
  formRef,
}: UseAdminUserCreateFormOptions) => {
  const navigate = useNavigate({ from: Route.fullPath });
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const createUserMutation = useAdminUserCreateMutation();
  const [isNextStepDisabled, setIsNextStepDisabled] = useState(false);

  const handleSubmitClick = useCallback(async () => {
    await formRef.current?.submitForm();
  }, [formRef]);

  const handleViewUserProfile = useCallback(
    async (userId: number) => {
      await navigate({
        to: Route.fullPath,
        params: {
          userId,
        },
        search: (prev) => ({
          ...prev,
        }),
      });
    },
    [navigate],
  );

  const handleCreateUser = useCallback(
    async (values: UserSubmitValues) => {
      setIsNextStepDisabled(true);
      try {
        const response = await createUserMutation.mutateAsync(values);

        await queryClient.invalidateQueries({
          queryKey: ['admin-user-search'],
        });

        closeModal('AdminUserCreate');

        openModal('AdminUserAdded', {
          name: `${response.firstName} ${response.lastName}`,
          id: response.id,
          onViewUserProfile: handleViewUserProfile,
          testID: 'admin-user-added-modal',
        });
      } catch (error) {
        console.error('Error creating admin user:', error);

        showToast({
          message: `There was an error creating the user: ${error instanceof Error ? error.message : 'Unknown error'}`,
          icon: <IconStatusIncorrect accentColor="$support.danger.default" />,
        });
      } finally {
        setIsNextStepDisabled(false);
      }
    },
    [
      closeModal,
      openModal,
      createUserMutation,
      queryClient,
      showToast,
      handleViewUserProfile,
    ],
  );

  return {
    handleSubmitClick,
    handleCreateUser,
    isFormLoading: createUserMutation.isPending,
    isNextStepDisabled,
  };
};
